import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiClient } from '@/utils/api'

export const useAuthStore = defineStore('auth', () => {
  const token = ref(localStorage.getItem('token'))
  const user = ref(null)
  const company = ref(null)
  const loading = ref(false)
  const error = ref(null)

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // Initialize API client with token
  if (token.value) {
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token.value}`
  }

  async function login(email, password) {
    loading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/auth/login', { email, password })
      const { token: newToken, user: userData } = response.data

      token.value = newToken
      user.value = userData

      localStorage.setItem('token', newToken)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      return true
    } catch (err) {
      const code = err.response?.data?.code
      if (code === 'PENDING_INVITATION') {
        error.value = 'Your invitation has not been accepted yet. Please check your email inbox (and spam folder) for the activation link.'
      } else {
        error.value = err.response?.data?.error || 'Login failed'
      }
      return false
    } finally {
      loading.value = false
    }
  }

  async function setup(companyData, adminData) {
    loading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/auth/setup', {
        company: companyData,
        admin: adminData
      })

      const { token: newToken, user: userData, company: responseCompany } = response.data

      token.value = newToken
      user.value = userData
      company.value = responseCompany

      localStorage.setItem('token', newToken)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      return true
    } catch (err) {
      error.value = err.response?.data?.error || 'Setup failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function signup(companyData, adminData) {
    loading.value = true
    error.value = null

    try {
      const response = await apiClient.post('/auth/signup', {
        company: companyData,
        admin: adminData
      })

      const { token: newToken, user: userData, company: responseCompany } = response.data

      token.value = newToken
      user.value = userData
      company.value = responseCompany

      localStorage.setItem('token', newToken)
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`

      return true
    } catch (err) {
      error.value = err.response?.data?.error || 'Signup failed'
      return false
    } finally {
      loading.value = false
    }
  }

  async function fetchUser() {
    if (!token.value) return false

    loading.value = true
    try {
      const response = await apiClient.get('/auth/me')
      user.value = response.data.user
      company.value = response.data.user?.company || null
      return true
    } catch (err) {
      console.error('Failed to fetch user:', err.response?.data?.error || err.message)
      if (err.response?.status === 401) {
        logout()
      }
      return false
    } finally {
      loading.value = false
    }
  }

  function logout() {
    token.value = null
    user.value = null
    company.value = null
    error.value = null

    localStorage.removeItem('token')
    delete apiClient.defaults.headers.common['Authorization']
  }

  async function updateProfile({ firstName, lastName }) {
    if (!user.value?.id) return false
    loading.value = true
    error.value = null
    try {
      const response = await apiClient.put(`/users/${user.value.id}`, { firstName, lastName })
      user.value = { ...user.value, ...response.data.user }
      return true
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to update profile'
      return false
    } finally {
      loading.value = false
    }
  }

  async function changePassword({ currentPassword, newPassword }) {
    loading.value = true
    error.value = null
    try {
      await apiClient.post('/auth/change-password', { currentPassword, newPassword })
      return true
    } catch (err) {
      error.value = err.response?.data?.error || 'Failed to change password'
      return false
    } finally {
      loading.value = false
    }
  }

  return {
    token,
    user,
    company,
    loading,
    error,
    isAuthenticated,
    login,
    setup,
    signup,
    logout,
    fetchUser,
    updateProfile,
    changePassword
  }
})
